import { useGetNetFlowsQuery, useGetSentimentScoresQuery, useGetLeadLagQuery, useGetSettingsQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StalenessBadge } from "@/components/domain/StalenessBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { PnLPill } from "@/components/domain/PnLPill";
import { fmtUsd, fmtTime } from "@/lib/format";
import { useMemo } from "react";

/**
 * V5 Intelligence: regime context only — net flows, RSS sentiment, lead-lag.
 * Treat every signal here as a *bias on the gatekeeper*, never as a direct
 * entry trigger.
 */
export default function Intelligence() {
  const settings = useGetSettingsQuery();
  const netflows = useGetNetFlowsQuery(200, { pollingInterval: 60000 });
  const sentiment = useGetSentimentScoresQuery(200, { pollingInterval: 60000 });
  const leadLag = useGetLeadLagQuery(200, { pollingInterval: 60000 });

  const netflowsLatest = useMemo(() => latestPerKey(netflows.data ?? [], "asset"), [netflows.data]);
  const sentimentLatest = useMemo(() => latestPerKey(sentiment.data ?? [], "symbol"), [sentiment.data]);
  // Most-recent timestamp per panel — drives the staleness badge.
  const netflowsLatestTs = useMemo(
    () => (netflows.data ?? []).reduce<string | null>(
      (m, r) => (r.fetched_at && (!m || r.fetched_at > m) ? r.fetched_at : m), null),
    [netflows.data]);
  const sentimentLatestTs = useMemo(
    () => (sentiment.data ?? []).reduce<string | null>(
      (m, r) => (r.computed_at && (!m || r.computed_at > m) ? r.computed_at : m), null),
    [sentiment.data]);
  const leadLagLatestTs = useMemo(
    () => (leadLag.data ?? []).reduce<string | null>(
      (m: string | null, r: any) => (r.computed_at && (!m || r.computed_at > m) ? r.computed_at : m), null),
    [leadLag.data]);

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3 text-xs">
        <Toggle label="Net flows" enabled={!!settings.data?.intelligence_netflow_enabled} />
        <Toggle label="Sentiment" enabled={!!settings.data?.intelligence_sentiment_enabled} />
        <Toggle label="Lead-lag" enabled={!!settings.data?.scalp_lead_lag_enabled} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>Exchange net flows (latest per asset)</span>
            <StalenessBadge ts={netflowsLatestTs} warnHours={1} staleHours={6} veryStaleHours={24} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <StaleNote ts={netflowsLatestTs} />
          {netflows.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : netflowsLatest.length === 0 ? (
            <p className="text-sm text-muted">
              No net-flow rows yet. Enable in Settings → V5 → Intelligence and configure an
              Etherscan API key in <code>.env</code>.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted">
                <tr>
                  <th className="text-left py-1">Asset</th>
                  <th className="text-right">Window</th>
                  <th className="text-right">Inflow</th>
                  <th className="text-right">Outflow</th>
                  <th className="text-right">Net</th>
                  <th className="text-left">Source</th>
                  <th className="text-left">Fetched</th>
                </tr>
              </thead>
              <tbody>
                {netflowsLatest.map((nf) => (
                  <tr key={nf.id} className="border-t border-border">
                    <td className="py-1">{nf.asset}</td>
                    <td className="text-right tabular">{nf.window_hours}h</td>
                    <td className="text-right tabular">{fmtUsd(nf.inflow_usd ?? null)}</td>
                    <td className="text-right tabular">{fmtUsd(nf.outflow_usd ?? null)}</td>
                    <td className="text-right">
                      <PnLPill value={nf.net_inflow_usd ?? null} />
                    </td>
                    <td className="text-xs text-muted">{nf.source ?? "—"}</td>
                    <td className="text-xs text-muted">{fmtTime(nf.fetched_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-xs text-muted mt-2">
            Persistent <strong>positive net inflow</strong> = bearish bias; persistent
            outflow = bullish. Reading any single row as a trade signal is wrong.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>Sentiment scores (latest per symbol, RSS-fed)</span>
            <StalenessBadge ts={sentimentLatestTs} warnHours={2} staleHours={12} veryStaleHours={48} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <StaleNote ts={sentimentLatestTs} />
          {sentiment.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : sentimentLatest.length === 0 ? (
            <p className="text-sm text-muted">
              No sentiment rows yet. Enable in Settings → V5 → Intelligence; the runner
              fetches RSS feeds on a 30-min cadence and uses Gemini Flash for classification.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted">
                <tr>
                  <th className="text-left py-1">Symbol</th>
                  <th className="text-right">Score</th>
                  <th className="text-right">Items</th>
                  <th className="text-right">Window</th>
                  <th className="text-left">Computed</th>
                </tr>
              </thead>
              <tbody>
                {sentimentLatest.map((ss) => (
                  <tr key={ss.id} className="border-t border-border">
                    <td className="py-1">{ss.symbol}</td>
                    <td className="text-right">
                      <span className={ss.score > 0 ? "text-good" : ss.score < 0 ? "text-bad" : "text-muted"}>
                        {ss.score > 0 ? "+" : ""}
                        {ss.score.toFixed(2)}
                      </span>
                    </td>
                    <td className="text-right tabular">{ss.n_items}</td>
                    <td className="text-right tabular">{ss.window_hours}h</td>
                    <td className="text-xs text-muted">{fmtTime(ss.computed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-xs text-muted mt-2">
            <strong>Honesty check.</strong> RSS lags Twitter/X by hours and lags price by even
            more. Useful as <em>regime context</em> (biases the gatekeeper); not as an entry
            trigger. Score range is −1 to +1.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>Lead-lag matrix (Hayashi–Yoshida cross-correlation)</span>
            <StalenessBadge ts={leadLagLatestTs} warnHours={1} staleHours={4} veryStaleHours={24} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <StaleNote ts={leadLagLatestTs} />
          {leadLag.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : !leadLag.data || leadLag.data.length === 0 ? (
            <p className="text-sm text-muted">
              No lead-lag rows yet. Enable in Settings → V5 → Lead-lag. The compute job runs
              every <code>scalp_lead_lag_compute_interval_minutes</code> minutes and feeds a
              score-bonus into the snapshot prioritizer.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted">
                <tr>
                  <th className="text-left py-1">Computed</th>
                  <th className="text-left">Leader</th>
                  <th className="text-left">Follower</th>
                  <th className="text-right">Lag (s)</th>
                  <th className="text-right">|Corr|</th>
                  <th className="text-right">N obs</th>
                  <th className="text-right">Window</th>
                </tr>
              </thead>
              <tbody>
                {leadLag.data.slice(0, 50).map((e) => (
                  <tr key={e.id} className="border-t border-border">
                    <td className="py-1 text-xs text-muted">{fmtTime(e.computed_at)}</td>
                    <td>{e.leader}</td>
                    <td>{e.follower}</td>
                    <td className="text-right tabular">{e.lag_seconds.toFixed(1)}</td>
                    <td className="text-right tabular">{e.correlation.toFixed(3)}</td>
                    <td className="text-right tabular">{e.n_obs}</td>
                    <td className="text-right tabular">{e.window_min}m</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-xs text-muted mt-2">
            Sub-second lags between BTC majors and alt followers are real but{" "}
            <strong>not exploitable from retail latency</strong>. The prioritizer uses these
            edges to bump follower pairs into the top-K when the leader has just moved.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

const STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

/** Amber inline note when the newest row in a panel is older than 7 days —
 *  the collector jobs have stalled before (no fresh rows since ~May 13). */
function StaleNote({ ts }: { ts: string | null }) {
  if (!ts) return null;
  let dt = new Date(ts);
  if (isNaN(dt.getTime())) dt = new Date(ts.replace(" ", "T"));
  if (isNaN(dt.getTime())) return null;
  if (Date.now() - dt.getTime() < STALE_AFTER_MS) return null;
  return (
    <p className="text-xs text-warn mb-2">
      No fresh data since {dt.toISOString().slice(0, 10)} — the collector job
      may be stalled.
    </p>
  );
}

function Toggle({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div className="rounded-md border border-border px-3 py-2 flex items-center justify-between">
      <span className="text-muted">{label}</span>
      <span className={enabled ? "text-good" : "text-muted"}>
        {enabled ? "ENABLED" : "OFF"}
      </span>
    </div>
  );
}

function latestPerKey<T extends { id: number; computed_at?: string; fetched_at?: string }>(
  rows: T[],
  key: keyof T,
): T[] {
  // Rows arrive newest-first from the API; keep only the first per key.
  const seen = new Set<string>();
  const out: T[] = [];
  for (const r of rows) {
    const k = String(r[key]);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}
